/**
 * The Dashboard headlines block — operator announcements (#490).
 *
 * A feed of discrete announcements an operator posts and the other reads walking
 * in the door: "here's what's new, here's how the system works." Pinned items
 * (standing reference) sort to the top; recently-posted items carry a "New" badge.
 * Any operator can post, edit, or delete — the block renders only inside the
 * authed Shell, so every viewer is an operator. The OPERATOR-facing surface (#490),
 * distinct from the firm-facing case-portal announcements page.
 *
 * Read/writes the worker's /api/announcements (see lib/use-announcements.ts).
 */

import { useState } from "react";
import { addDays, todayInPT } from "@command-center/shared";
import type { Announcement, AnnouncementInput } from "@command-center/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  useAnnouncements,
  useCreateAnnouncement,
  useDeleteAnnouncement,
  useUpdateAnnouncement,
} from "@/lib/use-announcements";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** YYYY-MM-DD → "Jun 22" (parsed by parts, so no timezone drift). */
function formatDate(ymd: string): string {
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return ymd;
  return `${MONTHS[m - 1]} ${d}`;
}

/** True when postedAt is today or within the prior 3 days (the "New" window).
 *  PT-anchored string compare, matching the server's date stamping — no
 *  local-timezone drift on the badge for a non-PT operator. */
function isRecent(postedAt: string, today = todayInPT()): boolean {
  return postedAt >= addDays(today, -3) && postedAt <= today;
}

export function AnnouncementsBlock() {
  const query = useAnnouncements();
  const [composing, setComposing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const items = query.data?.items ?? [];

  return (
    <section data-testid="announcements" className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <h2 className="font-serif text-xl">Announcements</h2>
        {!composing && editingId === null ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setComposing(true)}
            data-testid="announcement-post-btn"
          >
            + Post announcement
          </Button>
        ) : null}
      </header>

      {composing ? (
        <Composer
          onClose={() => setComposing(false)}
          onDone={() => setComposing(false)}
        />
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-plane-muted">Loading…</p>
      ) : items.length === 0 && !composing ? (
        <p
          className="text-sm text-plane-muted"
          data-testid="announcements-empty"
        >
          No announcements yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((item) =>
            editingId === item.id ? (
              <li key={item.id}>
                <Composer
                  initial={item}
                  onClose={() => setEditingId(null)}
                  onDone={() => setEditingId(null)}
                />
              </li>
            ) : (
              <li key={item.id}>
                <AnnouncementCard
                  item={item}
                  onEdit={() => setEditingId(item.id)}
                />
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  );
}

function AnnouncementCard({
  item,
  onEdit,
}: {
  item: Announcement;
  onEdit: () => void;
}) {
  const del = useDeleteAnnouncement();

  return (
    <Card className="p-4" data-testid="announcement-item">
      <div className="mb-1 flex items-center gap-2">
        {item.pinned ? (
          <Badge variant="amber" data-testid="announcement-pinned-badge">
            Pinned
          </Badge>
        ) : null}
        {isRecent(item.postedAt) ? (
          <Badge variant="blue" data-testid="announcement-new-badge">
            New
          </Badge>
        ) : null}
        <span className="font-medium">{item.title}</span>
        <span className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            data-testid={`announcement-edit-${item.id}`}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => del.mutate(item.id)}
            disabled={del.isPending}
            data-testid={`announcement-delete-${item.id}`}
          >
            Delete
          </Button>
        </span>
      </div>
      {item.body ? (
        <p className="whitespace-pre-wrap text-sm text-foreground">{item.body}</p>
      ) : null}
      <p className="mt-2 text-xs text-muted-foreground">
        {item.author} · {formatDate(item.postedAt)}
        {item.updatedAt ? ` · edited ${formatDate(item.updatedAt)}` : ""}
      </p>
    </Card>
  );
}

/** Create (no `initial`) or edit (with `initial`) form. Seeds its draft once. */
function Composer({
  initial,
  onClose,
  onDone,
}: {
  initial?: Announcement;
  onClose: () => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(() => initial?.title ?? "");
  const [body, setBody] = useState(() => initial?.body ?? "");
  const [pinned, setPinned] = useState(() => initial?.pinned ?? false);
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();

  const pending = create.isPending || update.isPending;
  const isError = create.isError || update.isError;

  function onSave() {
    if (title.trim() === "") return;
    const input: AnnouncementInput = { title, body, pinned };
    if (initial) {
      update.mutate({ id: initial.id, input }, { onSuccess: onDone });
    } else {
      create.mutate(input, { onSuccess: onDone });
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4" data-testid="announcement-composer">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Headline"
        data-testid="announcement-title-input"
      />
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Details (optional)"
        data-testid="announcement-body-input"
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={pinned}
          onChange={(e) => setPinned(e.target.checked)}
          data-testid="announcement-pinned-input"
        />
        Pin to top
      </label>
      <div className="flex items-center gap-3">
        <Button
          onClick={onSave}
          disabled={pending || title.trim() === ""}
          data-testid="announcement-save"
        >
          {pending ? "Saving…" : initial ? "Save" : "Post"}
        </Button>
        <Button
          variant="ghost"
          onClick={onClose}
          data-testid="announcement-cancel"
        >
          Cancel
        </Button>
        {isError ? (
          <span className="text-sm text-error" data-testid="announcement-error">
            Couldn't save — try again.
          </span>
        ) : null}
      </div>
    </Card>
  );
}
